/* THE EXAMPLE WORKSPACE'S WORLD — every row MADE UP, generated in the browser relative to TODAY (2026-09-28, his
 * choice A for coaches with no clients: *"mock clients … see how the workout reviews happen … mock data for
 * everything"*).
 *
 * 🔴 NOTHING HERE IS A REAL PERSON OR A REAL RECORD, AND NOTHING HERE IS EVER WRITTEN ANYWHERE. The rows live in this
 * tab's memory, are answered by the SAME PostgREST emulator the dev harness uses (`./rest.ts`), and `./mode.ts` refuses
 * every write before a request could leave the browser. The only real row is the signed-in coach's own profile (their
 * name on their own card), read once before the example starts.
 *
 * ⚠️ WHY IT IS GENERATED AND NOT A FILE: a review queue, a "missed" day and a "nothing after Sunday" line are all
 * sentences about TODAY. A frozen file would read "missed" for every day the week after it was written. So every date
 * is an OFFSET from the day the example is opened, and the week's shape is the same whichever day that is.
 *
 * ⚠️ THE VOCABULARIES ARE THE LIVE ONES, MEASURED 2026-09-28 on the render fixture — never invented: a session exercise's
 * `coach_exercise_id` is the LIBRARY row (522 of 522), `section_role` is `warm_up | main | cool_down`, a warm-up set logs
 * `status: "warmup"`, a review is a `coach_messages` row with `kind: "review"` naming its `session_id`, and an
 * assignment points at the client's own COPY of a template (`root_workout_id` = the template). A world spelled any other
 * way renders a working screen of the wrong shape, which is the vocabulary-mismatch family exactly.
 */

import type { Database } from "../database.types";
import type { Fixture, Rows } from "./rest";
import { commentBody, reviewBody, reviewTitle } from "../reviewBody";

type PublicTables = Database["public"]["Tables"];
/** A row of a real table, every column name checked by the compiler against the generated types — so a column this
 *  world spells differently from the live database is a BUILD error, never a quietly-null cell. */
type TableRow<T extends keyof PublicTables> = Partial<PublicTables[T]["Row"]>;

export type ExampleCoach = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
};

type Row = Record<string, unknown>;

/** Deterministic ids: `00000000-e1a0-4000-8000-<kind><n>` — recognisable on sight as the example's, and stable across
 *  reloads so an address like `#/clients/<id>` still opens the same person. */
const exampleId = (kind: number, n: number): string =>
  `00000000-e1a0-4000-8000-${String(kind).padStart(3, "0")}${String(n).padStart(9, "0")}`;

/** Every id this module mints starts with this, so a test (and a reader) can tell an example row from a real one. */
export const EXAMPLE_ID_PREFIX = "00000000-e1a0-4000-8000-";

type Tracking = "weight" | "reps" | "time";
/** `oneRM` — W125's switch per exercise (`[one_rm_enabled, body_is_load]`); absent = OFF, as every live row starts. */
type LibraryEntry = { key: string; name: string; tracking: Tracking[]; pattern: string; muscles: string[]; equipment: string[]; category: string; cue: string; oneRM?: [boolean, boolean] };

const LIBRARY: readonly LibraryEntry[] = [
  { key: "squat", name: "Back Squat", tracking: ["weight", "reps"], pattern: "squat", muscles: ["quadriceps", "glutes"], equipment: ["barbell", "squatRack"], category: "strength", cue: "Brace, sit between the hips, drive the floor away.", oneRM: [true, false] },
  { key: "rdl", name: "Romanian Deadlift", tracking: ["weight", "reps"], pattern: "hinge", muscles: ["hamstrings", "glutes"], equipment: ["barbell"], category: "strength", cue: "Soft knees, push the hips back, bar close to the legs." },
  { key: "bench", name: "Bench Press", tracking: ["weight", "reps"], pattern: "horizontal_push", muscles: ["chest", "triceps"], equipment: ["barbell", "bench"], category: "strength", cue: "Shoulder blades pinned, bar to the lower chest." },
  { key: "row", name: "Barbell Row", tracking: ["weight", "reps"], pattern: "horizontal_pull", muscles: ["latissimus dorsi", "upper back"], equipment: ["barbell"], category: "strength", cue: "Flat back, pull to the belly button." },
  { key: "ohp", name: "Overhead Press", tracking: ["weight", "reps"], pattern: "vertical_push", muscles: ["shoulders", "triceps"], equipment: ["barbell"], category: "strength", cue: "Squeeze the glutes, head through at the top." },
  { key: "pullup", name: "Pull-Up", tracking: ["weight", "reps"], pattern: "vertical_pull", muscles: ["latissimus dorsi", "biceps"], equipment: ["pull_up_bar"], category: "bodyweight", cue: "From a dead hang, chest to the bar.", oneRM: [true, true] },
  { key: "lunge", name: "Walking Lunge", tracking: ["weight", "reps"], pattern: "lunge", muscles: ["quadriceps", "glutes"], equipment: ["dumbbells"], category: "strength", cue: "Long step, back knee kisses the floor." },
  { key: "thrust", name: "Hip Thrust", tracking: ["weight", "reps"], pattern: "hinge", muscles: ["glutes"], equipment: ["barbell", "bench"], category: "strength", cue: "Chin tucked, ribs down, pause at the top." },
  { key: "incline", name: "Incline Dumbbell Press", tracking: ["weight", "reps"], pattern: "horizontal_push", muscles: ["chest", "shoulders"], equipment: ["dumbbells", "bench"], category: "strength", cue: "Elbows at 45°, press up and slightly in." },
  { key: "cablerow", name: "Seated Cable Row", tracking: ["weight", "reps"], pattern: "horizontal_pull", muscles: ["upper back", "biceps"], equipment: ["cable"], category: "strength", cue: "Tall chest, elbows past the ribs." },
  { key: "facepull", name: "Face Pull", tracking: ["weight", "reps"], pattern: "horizontal_pull", muscles: ["rear delts", "upper back"], equipment: ["cable"], category: "strength", cue: "Pull to the eyes, thumbs back." },
  { key: "goblet", name: "Goblet Squat", tracking: ["weight", "reps"], pattern: "squat", muscles: ["quadriceps"], equipment: ["kettlebell"], category: "strength", cue: "Elbows inside the knees, sit tall." },
  { key: "pushup", name: "Push-Up", tracking: ["reps"], pattern: "horizontal_push", muscles: ["chest", "triceps"], equipment: [], category: "bodyweight", cue: "One straight line from head to heels." },
  { key: "deadbug", name: "Dead Bug", tracking: ["reps"], pattern: "core", muscles: ["abs"], equipment: [], category: "bodyweight", cue: "Low back stays on the floor." },
  { key: "plank", name: "Plank", tracking: ["time"], pattern: "core", muscles: ["abs"], equipment: [], category: "bodyweight", cue: "Squeeze glutes and quads, breathe." },
  { key: "kneeraise", name: "Hanging Knee Raise", tracking: ["reps"], pattern: "core", muscles: ["abs", "hip flexors"], equipment: ["pullUpBar"], category: "bodyweight", cue: "No swing, curl the pelvis up." },
  { key: "hipstretch", name: "90/90 Hip Stretch", tracking: ["time"], pattern: "mobility", muscles: ["hips"], equipment: [], category: "mobility", cue: "Sit tall, lean over the front shin." },
  { key: "bike", name: "Bike Intervals", tracking: ["time"], pattern: "conditioning", muscles: ["legs"], equipment: ["bike"], category: "conditioning", cue: "Hard 30 s, easy 30 s." },
];

type PlanSet = { reps?: number; kg?: number; secs?: number; warm?: boolean };
type PlanExercise = { ex: string; sets: PlanSet[]; pairNext?: boolean; note?: string };
/** `purpose` is the part's one line (`coach_workout_sections.description`, what the phone shows as the section's note). */
type PlanSection = { name: string; type: "warm_up" | "workout" | "cool_down"; purpose?: string; exercises: PlanExercise[] };
type Template = { key: string; name: string; description: string; sections: PlanSection[] };

const reps = (n: number, count: number, kg?: number): PlanSet[] =>
  Array.from({ length: count }, () => (kg === undefined ? { reps: n } : { reps: n, kg }));
const holds = (secs: number, count: number): PlanSet[] => Array.from({ length: count }, () => ({ secs }));

const TEMPLATES: readonly Template[] = [
  {
    key: "fba", name: "Full Body A", description: "Squat day. Leave one or two reps in the tank on every working set.",
    sections: [
      { name: "Warm-up", type: "warm_up", purpose: "Gets them ready — done first, lighter.", exercises: [{ ex: "goblet", sets: reps(10, 2, 12) }, { ex: "deadbug", sets: reps(8, 2) }] },
      { name: "Main work", type: "workout", purpose: "The work this day is for.", exercises: [
        { ex: "squat", sets: [{ reps: 5, kg: 40, warm: true }, ...reps(5, 3, 60)], note: "Film the last set from the side." },
        { ex: "bench", sets: reps(6, 3, 40) },
        { ex: "row", sets: reps(8, 3, 40) },
      ] },
      { name: "Finisher", type: "cool_down", purpose: "Short, to close the day.", exercises: [{ ex: "plank", sets: holds(40, 2) }] },
    ],
  },
  {
    key: "fbb", name: "Full Body B", description: "Hinge day. Slow on the way down.",
    sections: [
      { name: "Warm-up", type: "warm_up", exercises: [{ ex: "pushup", sets: reps(10, 2) }, { ex: "hipstretch", sets: holds(30, 2) }] },
      { name: "Main work", type: "workout", exercises: [
        { ex: "rdl", sets: reps(8, 3, 50) },
        { ex: "ohp", sets: reps(6, 3, 30) },
        { ex: "pullup", sets: reps(6, 3) },
      ] },
      { name: "Accessories", type: "workout", purpose: "Back to back, rest after the pair.", exercises: [
        { ex: "lunge", sets: reps(10, 3, 10), pairNext: true },
        { ex: "facepull", sets: reps(15, 3, 15) },
      ] },
    ],
  },
  {
    key: "upper", name: "Upper", description: "Push and pull, balanced.",
    sections: [
      { name: "Warm-up", type: "warm_up", exercises: [{ ex: "facepull", sets: reps(15, 2, 10) }] },
      { name: "Main work", type: "workout", exercises: [
        { ex: "bench", sets: reps(8, 4, 50) },
        { ex: "pullup", sets: reps(6, 4, 10) },
        { ex: "incline", sets: reps(10, 3, 18), pairNext: true },
        { ex: "cablerow", sets: reps(12, 3, 45) },
      ] },
    ],
  },
  {
    key: "lower", name: "Lower", description: "Legs and trunk.",
    sections: [
      { name: "Warm-up", type: "warm_up", exercises: [{ ex: "goblet", sets: reps(10, 2, 16) }] },
      { name: "Main work", type: "workout", exercises: [
        { ex: "squat", sets: reps(8, 4, 70) },
        { ex: "rdl", sets: reps(10, 3, 60) },
        { ex: "thrust", sets: reps(10, 3, 70) },
      ] },
      { name: "Core", type: "cool_down", exercises: [{ ex: "kneeraise", sets: reps(12, 3) }] },
    ],
  },
  {
    key: "conditioning", name: "Conditioning", description: "Short and hard. Stop if form goes.",
    sections: [
      { name: "Intervals", type: "workout", exercises: [{ ex: "bike", sets: holds(600, 1) }, { ex: "plank", sets: holds(45, 3) }] },
    ],
  },
];

/** One made-up client. `pattern` is the days they train, as offsets from TODAY; `plan` names the template for each. */
type ClientSpec = {
  n: number; first: string; last: string; goals: string[]; days: number; joinedDaysAgo: number;
  /** [offset from today, template key, what happened] — `trained`, `partial` (two sets skipped), `missed`, or `due`. */
  schedule: [number, string, "trained" | "partial" | "missed" | "due"][];
  /** How much heavier/lighter than the template this person lifts. */
  load: number;
  weightKg: number;
};

const CLIENTS: readonly ClientSpec[] = [
  {
    n: 1, first: "Maria", last: "Ioannou", goals: ["strength"], days: 3, joinedDaysAgo: 70, load: 0.9, weightKg: 63,
    schedule: [[-13, "fba", "trained"], [-11, "fbb", "trained"], [-9, "fba", "trained"], [-6, "fbb", "trained"], [-4, "fba", "trained"], [-2, "fbb", "trained"],
               [1, "fba", "due"], [3, "fbb", "due"], [5, "fba", "due"], [8, "fbb", "due"], [10, "fba", "due"]],
  },
  {
    n: 2, first: "Alex", last: "Carter", goals: ["muscle"], days: 4, joinedDaysAgo: 120, load: 1.15, weightKg: 82,
    schedule: [[-12, "upper", "trained"], [-11, "lower", "trained"], [-9, "upper", "trained"], [-8, "lower", "trained"],
               [-5, "upper", "trained"], [-4, "lower", "trained"], [-2, "upper", "trained"], [-1, "lower", "trained"],
               [1, "upper", "due"], [2, "lower", "due"], [4, "upper", "due"], [4, "conditioning", "due"], [5, "lower", "due"],
               [8, "upper", "due"], [9, "lower", "due"]],
    // W137 (R7): day +4 holds TWO workouts — Upper first, then Conditioning (`day_position` 0, 1) — so the example shows a
    // day with two in their stored order, the state the Week's drag makes.
  },
  {
    n: 3, first: "Nikos", last: "Pappas", goals: ["fitter"], days: 3, joinedDaysAgo: 45, load: 1.0, weightKg: 77,
    schedule: [[-13, "fba", "trained"], [-10, "conditioning", "trained"], [-7, "fbb", "trained"], [-3, "fba", "missed"], [-1, "conditioning", "partial"],
               [2, "fbb", "due"], [5, "fba", "due"]],
  },
  {
    n: 4, first: "Elena", last: "Markou", goals: ["mobility"], days: 2, joinedDaysAgo: 12, load: 0.75, weightKg: 58,
    schedule: [[-8, "fba", "trained"], [-5, "fbb", "trained"], [-2, "fba", "trained"], [1, "fbb", "due"]],
  },
];

const INVITED = { n: 5, first: "Chris", last: "Doyle", email: "chris@example.com" };

/* Which finished days the coach has ALREADY reviewed (by [client, offset]); the rest wait in the queue — four of them,
 * so the Dashboard shows a queue that is real work without being a wall. */
const REVIEWED = new Set(["1:-13", "1:-11", "1:-9", "1:-6", "1:-4", "2:-12", "2:-11", "2:-9", "2:-8", "2:-5", "2:-4", "3:-13", "3:-10", "3:-7", "4:-8", "4:-5"]);

const dayKey = (today: Date, offset: number): string => {
  const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
};
const at = (today: Date, offset: number, hour: number, minute = 0): string =>
  `${dayKey(today, offset)}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00+00:00`;

const roundKg = (kg: number): number => Math.round(kg / 2.5) * 2.5;

/** Build the whole example workspace for `coach`, as of `today`. Pure: the same inputs give the same rows. */
export function exampleWorld(coach: ExampleCoach, today: Date): Fixture {
  const t: Record<string, Rows> = {};
  const push = <T extends keyof PublicTables>(table: T, row: TableRow<T>) => { (t[table] ??= []).push(row as Row); };
  let serial = 0;
  const next = (kind: number) => exampleId(kind, ++serial);
  const created = at(today, -150, 9);

  // ── The coach ─────────────────────────────────────────────────────────────────────────────────────────────────
  push("profiles", {
    id: coach.id, first_name: coach.first_name, last_name: coach.last_name, avatar_url: coach.avatar_url,
    account_role: "coach", created_at: created, main_goals: [], goals: [], injuries_data: [], unit_system: "metric",
  });
  push("coach_profiles", {
    id: coach.id, display_name: [coach.first_name, coach.last_name].filter(Boolean).join(" ") || "Coach", is_published: true,
    headline: "Strength and conditioning", bio: "", avatar_url: coach.avatar_url, city: "", created_at: created, updated_at: created,
    achievements: [], certifications: [], schools: [], coaching_since: null, years_coaching: 6, instagram: null, website: null,
  });

  // ── The library ───────────────────────────────────────────────────────────────────────────────────────────────
  const libId = new Map<string, string>();
  for (const e of LIBRARY) {
    const id = next(1);
    libId.set(e.key, id);
    const isHold = e.tracking.includes("time");
    push("coach_exercises", {
      id, coach_id: coach.id, name: e.name, category: e.category, equipment: e.equipment, primary_muscles: e.muscles,
      movement_pattern: e.pattern, tracking_fields: e.tracking, instructions: [e.cue], tips: [], notes: e.cue,
      is_hold: isHold, hidden_at: null, difficulty: null, demo_video_url: null, demo_video_source: null, thumbnail_url: null,
      progression_config: null, progress_views: [], catalog_exercise_id: null, catalog_link_source: null, starter_key: null,
      one_rm_enabled: e.oneRM?.[0] === true, body_is_load: e.oneRM?.[1] === true,
      typical_hold_seconds_min: isHold ? 30 : null, typical_hold_seconds_max: isHold ? 45 : null,
      typical_rep_range_min: isHold ? null : 6, typical_rep_range_max: isHold ? null : 12, is_quality_limited: null,
      instructions_source: null, created_at: created, updated_at: created,
    });
  }
  const nameOf = new Map(LIBRARY.map((e) => [libId.get(e.key), e.name]));

  // ── Workouts: the templates, then each assignment's own copy (as `clone_coach_workout` makes them) ────────────
  type Written = { workoutId: string; flat: { rowId: string; libraryId: string; role: string; sets: PlanSet[] }[] };
  const writeWorkout = (tpl: Template, clientId: string | null, root: string | null, load: number): Written => {
    const workoutId = next(clientId === null ? 2 : 3);
    push("coach_workouts", {
      id: workoutId, coach_id: coach.id, client_id: clientId, root_workout_id: root, kind: "workout", name: tpl.name,
      description: tpl.description, archived_at: null, check_in_fields: null, task_prompt: null, task_optional: false, created_at: created, updated_at: created,
    });
    const flat: Written["flat"] = [];
    tpl.sections.forEach((sec, si) => {
      const sectionId = next(4);
      push("coach_workout_sections", {
        id: sectionId, workout_id: workoutId, name: sec.name, position: si, section_type: sec.type,
        format: sec.exercises.some((e) => e.pairNext) ? "superset" : "straight_sets", format_config: null, description: sec.purpose ?? null,
        is_implicit: false, reminder_times: [],
      });
      sec.exercises.forEach((pe, ei) => {
        const rowId = next(5);
        const libraryId = libId.get(pe.ex) ?? "";
        const sets = pe.sets.map((s) => (s.kg === undefined ? s : { ...s, kg: roundKg(s.kg * load) }));
        push("coach_workout_exercises", {
          id: rowId, section_id: sectionId, exercise_id: libraryId, position: ei, superset_with_next: pe.pairNext === true,
          notes: pe.note ?? null, each_side: false, hold_until_lost: false, sets: null, reps_min: null, reps_max: null, hold_seconds_min: null,
          hold_seconds_max: null, rest_seconds: null, tempo: null,
        });
        sets.forEach((s, k) => push("coach_workout_sets", {
          id: next(6), workout_exercise_id: rowId, position: k, set_type: s.warm === true ? "warm_up" : "regular",
          reps: s.reps ?? null, weight_kg: s.kg ?? null, time_seconds: s.secs ?? null, rest_seconds: 90, rounds: null, distance_m: null,
        }));
        flat.push({ rowId, libraryId, role: sec.type === "workout" ? "main" : sec.type, sets });
      });
    });
    return { workoutId, flat };
  };
  const templateId = new Map<string, string>();
  for (const tpl of TEMPLATES) templateId.set(tpl.key, writeWorkout(tpl, null, null, 1).workoutId);
  const taskId = next(2);
  push("coach_workouts", {
    id: taskId, coach_id: coach.id, client_id: null, root_workout_id: null, kind: "task", name: "Daily mobility",
    description: "Ten minutes, any time of day.", task_prompt: "Did you do your ten minutes?", archived_at: null,
    check_in_fields: null, task_optional: false, created_at: created, updated_at: created,
  });

  // ── The clients ───────────────────────────────────────────────────────────────────────────────────────────────
  for (const c of CLIENTS) {
    const clientId = exampleId(7, c.n);
    const linkId = exampleId(8, c.n);
    push("coach_clients", {
      id: linkId, coach_id: coach.id, client_id: clientId, status: "active", invited_first_name: c.first,
      invited_last_name: c.last, invited_email: null, created_at: at(today, -c.joinedDaysAgo - 2, 10),
      accepted_at: at(today, -c.joinedDaysAgo, 10), invite_token: null, invite_token_expires_at: at(today, 30, 0),
      ended_at: null, auto_apply_next_week: false, visible_range: "all",
    });
    push("profiles", {
      id: clientId, first_name: c.first, last_name: c.last, account_role: "client", avatar_url: null,
      created_at: at(today, -c.joinedDaysAgo, 10), main_goals: c.goals, goals: c.goals, training_days_per_week: c.days,
      session_length_minutes: 60, weight_kg: c.weightKg, height_cm: null, injuries_data: [], unit_system: "metric",
      training_start_date: null, never_trained: false, timezone: "Europe/Athens", review_mode: "with_review",
    });
    // Three weigh-ins a fortnight apart, so the Body Metrics card has a line to draw.
    [-28, -14, -1].forEach((off, i) => push("body_measurements", {
      id: next(9), user_id: clientId, measured_at: at(today, off, 7), created_at: at(today, off, 7), updated_at: at(today, off, 7),
      weight_kg: Math.round((c.weightKg + (1 - i) * 0.6) * 10) / 10, notes: null, body_fat_percent: null, waist_cm: null,
      photo_front_path: null, photo_side_path: null, photo_back_path: null,
      // The N2 weekly check-in on the newest EXAMPLE weigh-in only (2026-10-08), so the panel has a week to draw.
      ...(off === -1
        ? { sleep_hours: 7.5, appetite: 3, training_desire: 4, life_event: false, life_event_note: null, water_tags: ["travel"] }
        : {}),
    }));

    for (const [off, key, what] of c.schedule) {
      const tpl = TEMPLATES.find((x) => x.key === key);
      if (tpl === undefined) continue;
      // The lifts climb: the most recent week is ~5 % heavier than the one before it, so Progress has a move to show.
      const copy = writeWorkout(tpl, clientId, templateId.get(key) ?? null, c.load * (off < -7 ? 1 : 1.05));
      const day = dayKey(today, off);
      const done = what === "trained" || what === "partial";
      const dayPosition = (t.coach_assignments ?? []).filter((r) => r.client_id === clientId && r.scheduled_date === day).length;
      const assignmentId = next(10);
      push("coach_assignments", {
        id: assignmentId, coach_id: coach.id, client_id: clientId, workout_id: copy.workoutId, root_workout_id: templateId.get(key) ?? null,
        scheduled_date: day, status: done ? "completed" : "assigned", hidden_from_client: false, is_deload: false, note: null,
        assigned_at: at(today, off - 7, 9), completed_at: done ? at(today, off, 18, 40) : null, completed_minutes: [], completed_snapshot: null,
        day_position: dayPosition,
      });
      if (!done) continue;

      // What they did: the plan, with the small misses and wins real training has.
      const sessionId = next(11);
      push("workout_sessions", {
        id: sessionId, user_id: clientId, kind: "training", status: "completed", scheduled_date: day,
        created_at: at(today, off, 17, 30), completed_at: at(today, off, 18, 40), updated_at: at(today, off, 18, 40),
        duration_actual_minutes: 70, week_number: 1, session_number: 1, performance_notes: "", ai_adjusted: false,
        ai_adjustment_reason: "", perceived_difficulty: null, program_id: null, program_session_id: null, readiness_score: null,
        // CARRY-FEEDBACK B — the phone names the assignment it performed (migration `20261019100000`).
        coach_assignment_id: assignmentId,
      });
      copy.flat.forEach((row, i) => {
        const wseId = next(12);
        const skipped = what === "partial" && i === copy.flat.length - 1;
        push("workout_session_exercises", {
          id: wseId, workout_session_id: sessionId, coach_exercise_id: row.libraryId, exercise_id: null, position: i + 1,
          section_role: row.role, status: skipped ? "skipped" : "completed", completion_status: skipped ? "skipped" : "completed",
          actual_sets: skipped ? 0 : row.sets.length, reps_per_set: [], weight_per_set_kg: [], hold_seconds_per_set: [], rpe_per_set: [],
          notes: "", per_exercise_note: "", created_at: at(today, off, 17, 30), updated_at: at(today, off, 18, 40),
          exercise_started_at: null, exercise_completed_at: at(today, off, 17, 35 + i * 4), was_swapped: false,
          swapped_from_exercise_id: null, program_session_exercise_id: null,
        });
        const firstMain = row.role === "main" && copy.flat.findIndex((f) => f.role === "main") === i;
        row.sets.forEach((s, k) => {
          const last = k === row.sets.length - 1;
          // A PR on Alex's heaviest recent day, a hard last set, one short set — the flags a coach scans for.
          const bump = c.n === 2 && off === -1 && firstMain && last ? 2.5 : 0;
          const short = c.n === 1 && off === -2 && firstMain && last;
          push("set_logs", {
            id: next(13), user_id: clientId, workout_session_exercise_id: wseId, set_number: k + 1,
            status: skipped ? "skipped" : s.warm === true ? "warmup" : "completed",
            reps_completed: skipped || s.reps === undefined ? null : short ? s.reps - 2 : s.reps,
            weight_kg: skipped || s.kg === undefined ? null : s.kg + bump,
            hold_seconds: skipped || s.secs === undefined ? null : s.secs,
            rpe: skipped ? null : firstMain && last ? (c.n === 2 ? 9 : 8) : null,
            note: firstMain && last && c.n === 1 && off === -2 ? "Last set felt heavy — slept badly." : c.n === 2 && off === -1 && firstMain && last ? "New best!" : "",
            completed_at: at(today, off, 17, 36 + i * 4 + k), assist_kg: null, equipment_used_id: null,
            modifier_label_override: "", modifier_value_numeric: null, round_seconds: null,
          });
        });
      });

      // The conversation around it.
      if (REVIEWED.has(`${c.n}:${off}`)) {
        push("coach_messages", {
          id: next(14), coach_id: coach.id, client_id: clientId, sender_id: coach.id, kind: "review", session_id: sessionId,
          exercise_row_id: null, coach_exercise_id: null, reply_to: null,
          // Written the way the REVIEW writes one (`reviewWrite`: `reviewBody(title, text)`, its head `Reviewed Friday's Full
          // Body A`) — 17 of 17 live reviews carry the head (MEASURED 2026-10-16); a bare body left the chat's card unnamed.
          body: reviewBody(reviewTitle(day, tpl.name), "Good session. Keep the same weights next time and own every rep."),
          created_at: at(today, off + 1, 8, 15), read_at: at(today, off + 1, 12), audio_path: null, audio_seconds: null, video_path: null, video_seconds: null,
        });
      }
      if (c.n === 1 && off === -4) {
        // A comment on one exercise, and the client's reply the coach has not answered yet.
        const squat = copy.flat.find((f) => f.role === "main");
        const squatName = [...libId].find(([, id]) => id === squat?.libraryId)?.[0] ?? "Exercise";
        const commentId = next(14);
        // W111 — written the way the REVIEW writes an exercise comment (`reviewWrite`: kind `comment`, the body titled by the
        // exercise, `reviewBody.commentBody`). As a plain `message` it matched no reader, so the example's reply waiting
        // never reached the Dashboard (rendered 2026-09-30: no reply row in the example's To do list).
        push("coach_messages", {
          id: commentId, coach_id: coach.id, client_id: clientId, sender_id: coach.id, kind: "comment", session_id: sessionId,
          exercise_row_id: squat?.rowId ?? null, coach_exercise_id: squat?.libraryId ?? null, reply_to: null,
          body: commentBody(squatName, "Your depth on the squat was great. Try to keep the chest a little taller out of the hole."),
          created_at: at(today, off + 1, 8, 10), read_at: at(today, off + 1, 12), audio_path: null, audio_seconds: null, video_path: null, video_seconds: null,
        });
        push("coach_messages", {
          id: next(14), coach_id: coach.id, client_id: clientId, sender_id: clientId, kind: "message", session_id: sessionId,
          exercise_row_id: squat?.rowId ?? null, coach_exercise_id: squat?.libraryId ?? null, reply_to: commentId,
          body: "Thanks! Should I go up next week or stay at this weight?",
          created_at: at(today, -1, 20, 5), read_at: null, audio_path: null, audio_seconds: null, video_path: null, video_seconds: null,
        });
      }
    }
  }

  // A plain chat thread, so the Inbox is not empty.
  const nikos = exampleId(7, 3);
  const chat: [string, string, number, number][] = [
    [coach.id, "Hi Nikos, how did the knee feel after Thursday?", -6, 9],
    [nikos, "Much better, no pain on the lunges.", -6, 13],
    [nikos, "I'm travelling on Friday, can I move that session?", -3, 19],
  ];
  chat.forEach(([sender, body, off, hour], i) => push("coach_messages", {
    id: next(14), coach_id: coach.id, client_id: nikos, sender_id: sender, kind: "message", session_id: null,
    exercise_row_id: null, coach_exercise_id: null, reply_to: null, body, created_at: at(today, off, hour, i),
    read_at: sender === coach.id ? at(today, off, hour + 1) : null, audio_path: null, audio_seconds: null, video_path: null, video_seconds: null,
  }));

  // The invited client, who has not accepted yet.
  push("coach_clients", {
    id: exampleId(8, INVITED.n), coach_id: coach.id, client_id: null, status: "pending", invited_first_name: INVITED.first,
    invited_last_name: INVITED.last, invited_email: INVITED.email, created_at: at(today, -2, 11), accepted_at: null,
    invite_token: "exampleinvitation0000000", invite_token_expires_at: at(today, 28, 0), ended_at: null,
    auto_apply_next_week: false, visible_range: "all",
  });

  // A private note, and two reminders.
  push("coach_client_notes", {
    coach_client_id: exampleId(8, 3), body: "Old left knee injury (2023). Lunges are fine, avoid deep jumps.", updated_at: at(today, -40, 10),
  });
  push("coach_reminders", {
    id: next(16), coach_id: coach.id, client_id: exampleId(7, 4), title: "Check in with Elena after her first two weeks",
    due_at: at(today, 0, 17), done_at: null, created_at: at(today, -3, 9),
  });
  push("coach_reminders", {
    id: next(16), coach_id: coach.id, client_id: null, title: "Plan next month's block", due_at: at(today, 4, 9),
    done_at: null, created_at: at(today, -1, 9),
  });

  // One program, the Library's third list.
  const programId = next(17);
  push("coach_programs", { id: programId, coach_id: coach.id, name: "4-week strength base", description: "Three full-body days a week.", archived_at: null, created_at: created, updated_at: created });
  [0, 1, 2, 3].forEach((week) => ([[0, "fba"], [2, "fbb"], [4, "fba"]] as const).forEach(([weekday, key]) => push("coach_program_days", {
    id: next(18), program_id: programId, week_index: week, weekday, position: 0, workout_id: templateId.get(key) ?? "", note: null,
  })));
  t.coach_program_assignments ??= [];
  t.coach_program_weeks ??= []; // F15 3.9: no row = a written week — the example program marks nothing
  t.coach_deload_proposals ??= []; // F15 4.2: the numbers have proposed no deload in the example

  // Tables the app reads that the example deliberately leaves empty — DECLARED, so an empty card is the example's
  // honest empty state and never the emulator's "unknown table" warning.
  for (const table of ["coach_requests", "coach_message_reactions", "coach_message_hidden", "coach_equipment", "exercise_coverage", "exercises", "coach_exercise_engine", "coach_workout_engine", "coach_workout_rules", "form_clips", "community_identities",
    // The reviewer seat (2026-10-10): the example coach reviews nobody — the `Reviewing` group draws nothing.
    "reviewer_reviews", "reviewer_review_items", "reviewer_week_proposals", "coach_calls",
    // W125 part 2 (b): nobody typed a tested max — the 1RM lifts draw `Tested max` + Add.
    "tested_maxes"]) t[table] ??= [];

  // Names resolved by the review reader through `coach_exercises` above; the engine catalogue stays empty.
  void nameOf;
  return { coachId: coach.id, tables: t };
}
